import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { ScrollText } from "lucide-react";
import { Pagination, SortableHeader, MultiSelectDropdown, ColumnVisibility, compareValues } from "../components/DataTable";
import useLocalStorageState from "../hooks/useLocalStorageState";

const ACTION_OPTIONS = [
  "user.create", "user.update", "user.deactivate",
  "password.self_change", "password.admin_reset",
  "service.create", "service.update", "service.delete", "service.approve", "service.reject",
  "portfolio.create", "portfolio.update", "portfolio.delete", "portfolio.approve", "portfolio.reject",
  "inquiry.status_change", "inquiry.delete",
  "ad.create", "ad.update", "ad.delete", "ad.approve", "ad.reject",
];

const COLUMNS = [
  { key: "created_at", label: "When", required: true },
  { key: "user_label", label: "User" },
  { key: "user_role", label: "Role" },
  { key: "action", label: "Action" },
  { key: "summary", label: "Summary" },
];

const FETCH_LIMIT = 200;

export default function AdminAuditLogs() {
  const [logs, setLogs] = useState([]);
  const [totalServer, setTotalServer] = useState(0);
  const [users, setUsers] = useState([]);
  const [userFilter, setUserFilter] = useState("");
  const [actionSel, setActionSel] = useState(new Set());
  const [loading, setLoading] = useState(true);

  const [sortKey, setSortKey] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [hiddenCols, setHiddenCols] = useLocalStorageState("admin_audit_hidden_cols_v1", new Set());
  const toggleCol = (k) => setHiddenCols((prev) => { const n = new Set(prev); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const showCol = (k) => !hiddenCols.has(k);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: 1, page_size: FETCH_LIMIT });
      if (userFilter) params.set("user_id", userFilter);
      const r = await api.get(`/audit-logs?${params}`);
      setLogs(r.data.items);
      setTotalServer(r.data.total);
    } finally { setLoading(false); }
  }, [userFilter]);
  useEffect(() => { api.get("/users").then((r) => setUsers(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const toggleAction = (a) => { setPage(1); setActionSel((prev) => { const n = new Set(prev); if (n.has(a)) n.delete(a); else n.add(a); return n; }); };
  const onSort = (key) => { if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc")); else { setSortKey(key); setSortDir("asc"); } };

  const filtered = useMemo(() => {
    if (actionSel.size === 0) return logs;
    return logs.filter((l) => actionSel.has(l.action));
  }, [logs, actionSel]);
  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => compareValues(a[sortKey], b[sortKey], sortDir));
    return arr;
  }, [filtered, sortKey, sortDir]);
  const total = sorted.length;
  const paged = useMemo(() => sorted.slice((page - 1) * pageSize, (page - 1) * pageSize + pageSize), [sorted, page, pageSize]);
  useEffect(() => { setPage(1); }, [pageSize, actionSel.size, userFilter]);

  return (
    <div data-testid="admin-audit-page" className="p-6 lg:p-10 w-full">
      <div className="mb-8">
        <div className="text-sm uppercase tracking-[0.2em] text-[#525252]">Activity</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2 flex items-center gap-3">
          <ScrollText size={32} strokeWidth={1.5} /> Audit Log
        </h1>
        <p className="text-sm text-[#525252] mt-2">
          {totalServer} total entries
          {totalServer > FETCH_LIMIT && (
            <span className="ml-2 text-amber-700">· Showing latest {FETCH_LIMIT}, narrow by user to see older</span>
          )}
        </p>
      </div>

      <div className="bg-white border border-black/10 p-4 mb-4 flex flex-wrap items-end gap-4">
        <div>
          <label className="block text-xs uppercase tracking-wider text-[#525252] mb-1">User</label>
          <select data-testid="filter-user" value={userFilter} onChange={(e) => setUserFilter(e.target.value)}
                  className="text-sm border border-black/10 rounded-sm px-3 py-2 min-w-[200px]">
            <option value="">All users</option>
            {users.map((u) => <option key={u.id} value={u.id}>{`${u.name} (${u.email || u.mobile || ""})`}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wider text-[#525252] mb-1">Action</label>
          <MultiSelectDropdown
            label="Action"
            options={ACTION_OPTIONS}
            selected={actionSel}
            onToggle={toggleAction}
            onClear={() => setActionSel(new Set())}
            testId="audit-action"
          />
        </div>
        <div className="ml-auto"><ColumnVisibility columns={COLUMNS} hidden={hiddenCols} onToggle={toggleCol} testId="audit-cols"/></div>
      </div>

      {loading ? <div className="text-[#525252]">Loading...</div>
       : sorted.length === 0 ? (
        <div className="bg-white border border-black/10 p-16 text-center text-[#525252]" data-testid="audit-empty">No log entries match the current filter.</div>
       ) : (
        <>
          <div className="bg-white border border-black/10 overflow-x-auto">
            <table className="w-full text-sm" data-testid="audit-table">
              <thead>
                <tr className="border-b border-black/10 bg-[#FAFAFA]">
                  {showCol("created_at") && <th className="px-4 py-3 text-left"><SortableHeader label="When" sortKey="created_at" currentSort={sortKey} currentDir={sortDir} onSort={onSort}/></th>}
                  {showCol("user_label") && <th className="px-4 py-3 text-left"><SortableHeader label="User" sortKey="user_label" currentSort={sortKey} currentDir={sortDir} onSort={onSort}/></th>}
                  {showCol("user_role") && <th className="px-4 py-3 text-left"><SortableHeader label="Role" sortKey="user_role" currentSort={sortKey} currentDir={sortDir} onSort={onSort}/></th>}
                  {showCol("action") && <th className="px-4 py-3 text-left"><SortableHeader label="Action" sortKey="action" currentSort={sortKey} currentDir={sortDir} onSort={onSort}/></th>}
                  {showCol("summary") && <th className="px-4 py-3 text-left"><SortableHeader label="Summary" sortKey="summary" currentSort={sortKey} currentDir={sortDir} onSort={onSort}/></th>}
                </tr>
              </thead>
              <tbody>
                {paged.map((l) => (
                  <tr key={l.id} data-testid={`audit-row-${l.id}`} className="border-b border-black/5 hover:bg-[#FAFAFA]">
                    {showCol("created_at") && <td className="px-4 py-3 text-[#525252] text-xs whitespace-nowrap">{new Date(l.created_at).toLocaleString()}</td>}
                    {showCol("user_label") && <td className="px-4 py-3 font-medium truncate max-w-[220px]" title={l.user_label}>{l.user_label}</td>}
                    {showCol("user_role") && <td className="px-4 py-3 text-[#525252] text-xs uppercase tracking-wider">{l.user_role}</td>}
                    {showCol("action") && <td className="px-4 py-3"><span className="text-xs font-mono bg-[#FAFAFA] border border-black/5 px-2 py-0.5 rounded-sm">{l.action}</span></td>}
                    {showCol("summary") && <td className="px-4 py-3 text-[#525252] truncate max-w-[420px]" title={l.summary}>{l.summary}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={page} pageSize={pageSize} total={total} onPageChange={setPage} onPageSizeChange={setPageSize}/>
        </>
       )}
    </div>
  );
}
